// Constants and pure helpers ported verbatim from the reference logic (design/unpacked/task-panes/logic.js).
import tokens from '@tokens/tokens.json';
import type { Task } from '@/domain/model';
import { calState } from './calendar';
import { lang, t } from '@/app/i18n';

const faNum = (n: number | string) => String(n).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[+d]!).replace('.', '٫');
const unit = (n: number | string, en: string, fa: string) => lang.value === 'fa' ? faNum(n) + ' ' + fa : n + en;

export type ThemeName = 'dark' | 'midnight' | 'mocha' | 'forest' | 'wine' | 'london' | 'light' | 'paper' | 'sage' | 'rose';
/** Design v8 themes with the contrast boost already applied (design/tokens/tokens.json `contrastBoost`). */
export const THEMES = tokens.themes as unknown as Record<ThemeName, Record<string, string>>;
export const THEME_ORDER = tokens.order as ThemeName[];
export const THEME_NAMES = tokens.names as Record<ThemeName, string>;
/** v10: a theme that brings its own accent (London → bus red), set when the theme is picked. */
export const THEME_ACCENT = tokens.themeAccent as Partial<Record<ThemeName, string>>;
export const MOBILE_W = 346;
export const PEEK = tokens.layout.mobilePeek;
export const COLORS = tokens.avatarColors;

/** A changelog item: plain text, or text with a screenshot (`img` = path under the app's public base). */
export type ChangelogItem = string | { text: string; img: string; ratio?: string };
/** The reference prototype's changelog (design v7) lives in referenceChangelog.ts (demo workspace only). */
export { REFERENCE_CHANGELOG } from './referenceChangelog';

/** Short random id used by the prototype for UI-local objects (panes). Entities use UUIDv7 (ids.ts). */
export const uid = (): string => Math.random().toString(36).slice(2, 9);

export const createdAt = (t: Task): number | null => ((t.log || []).find(e => e.k === 'create') || ({} as { at?: number })).at || null;
export const ageOf = (at: number | null): string => {
  if (!at) return '';
  const d = (Date.now() - at) / 864e5;
  if (d < 1) return 'today';
  if (d < 14) return unit(Math.floor(d), 'd', 'روز');
  if (d < 60) return unit(Math.round(d / 7), 'w', 'هفته');
  if (d < 365) return unit(Math.round(d / 30), 'mo', 'ماه');
  return unit((d / 365).toFixed(1).replace(/\.0$/, ''), 'y', 'سال');
};
export const SPECIAL: Record<string, string> = { __none: 'No tag', __oldest: 'Oldest first', __bm: 'Bookmarks' };
export const chipName = (k: string): string => SPECIAL[k] ? t(SPECIAL[k]!) : k === 'All' ? t('All') : k;
export const rel = (at: number): string => {
  const d = (Date.now() - at) / 1000;
  if (d < 45) return lang.value === 'fa' ? 'اکنون' : 'now';
  if (d < 3600) return unit(Math.round(d / 60), 'm', 'دقیقه');
  if (d < 86400) return unit(Math.round(d / 3600), 'h', 'ساعت');
  if (d < 604800) return unit(Math.round(d / 86400), 'd', 'روز');
  return new Date(at).toLocaleDateString(calState.loc, { calendar: calState.p, month: 'short', day: 'numeric' });
};
